import { createStoreModule } from '@next-qr/lib-ui/store';

// ─── Types ────────────────────────────────────────────────────────────────────

export type AuthView = null | 'login' | 'register' | 'forgot' | 'reset';

export interface UserInfo {
  id: number;
  name: string;
  email: string | null;
  is_guest?: boolean;
}

/** Minimal interface the API client must satisfy for auth operations. */
export interface AuthApiClient {
  login(credentials: { email: string; password: string }): Promise<UserInfo>;
  register(credentials: { email: string; name: string; password: string }): Promise<UserInfo>;
  forgotPassword(args: { email: string }): Promise<void>;
  resetPassword(args: { token: string; password: string }): Promise<UserInfo>;
  logout(): Promise<void>;
  getUser(): Promise<UserInfo | null>;
  isGuest(): Promise<boolean>;
}

export interface AuthState {
  authView: AuthView;
  email: string;
  name: string;
  password: string;
  confirmPassword: string;
  resetToken: string;
  newPassword: string;
  authLoading: boolean;
  authError: string | null;
  forgotSent: boolean;
  user: UserInfo | null;
  isGuest: boolean;
}

export interface AuthActions {
  setAuthView(v: AuthView): void;
  setEmail(s: string): void;
  setName(s: string): void;
  setPassword(s: string): void;
  setConfirmPassword(s: string): void;
  setResetToken(s: string): void;
  setNewPassword(s: string): void;
  setAuthError(e: string | null): void;
  resetAuthForm(): void;
  loadUser(): Promise<void>;
  handleLogin(): Promise<void>;
  handleRegister(): Promise<void>;
  handleForgotPassword(): Promise<void>;
  handleResetPassword(): Promise<void>;
  handleLogout(): Promise<void>;
}

export type AuthStore = AuthState & AuthActions;

// ─── Singleton client registry ────────────────────────────────────────────────

let _getAuthClient: (() => AuthApiClient) | null = null;

/**
 * Register the API client factory before any auth actions are dispatched.
 * Call once at app startup (e.g. in the store setup file of the consumer).
 */
export function initAuthClient(factory: () => AuthApiClient): void {
  _getAuthClient = factory;
}

function getApi(): AuthApiClient {
  if (!_getAuthClient) {
    throw new Error('[authModule] Auth client not initialized. Call initAuthClient() first.');
  }
  return _getAuthClient();
}

// ─── Store module ─────────────────────────────────────────────────────────────

export const authModule = createStoreModule({
  auth: (set: any, get: any): AuthStore => ({
    // ── State ───────────────────────────────────────────────────────────────
    authView: null,
    email: '',
    name: '',
    password: '',
    confirmPassword: '',
    resetToken: '',
    newPassword: '',
    authLoading: false,
    authError: null,
    forgotSent: false,
    user: null,
    isGuest: true,

    // ── Setters ─────────────────────────────────────────────────────────────
    setAuthView: (v: AuthView) => set({ authView: v }),
    setEmail: (s: string) => set({ email: s }),
    setName: (s: string) => set({ name: s }),
    setPassword: (s: string) => set({ password: s }),
    setConfirmPassword: (s: string) => set({ confirmPassword: s }),
    setResetToken: (s: string) => set({ resetToken: s }),
    setNewPassword: (s: string) => set({ newPassword: s }),
    setAuthError: (e: string | null) => set({ authError: e }),
    resetAuthForm: () =>
      set({ email: '', name: '', password: '', confirmPassword: '', resetToken: '', newPassword: '', authError: null, forgotSent: false }),

    // ── Async actions ────────────────────────────────────────────────────────
    async loadUser() {
      const [u, guest] = await Promise.all([getApi().getUser(), getApi().isGuest()]);
      set({ user: u, isGuest: guest });
    },

    async handleLogin() {
      const { email, password } = get() as AuthStore;
      if (!email.trim() || !password) return;
      set({ authLoading: true, authError: null });
      try {
        const userInfo = await getApi().login({ email: email.trim(), password });
        set({ user: userInfo, isGuest: false, authView: null });
        (get() as AuthStore).resetAuthForm();
      } catch (err: any) {
        set({ authError: err.message || 'Login failed' });
      } finally {
        set({ authLoading: false });
      }
    },

    async handleRegister() {
      const { email, name, password, confirmPassword } = get() as AuthStore;
      if (!email.trim() || !name.trim() || !password) return;
      if (password !== confirmPassword) {
        set({ authError: 'Passwords do not match' });
        return;
      }
      set({ authLoading: true, authError: null });
      try {
        const userInfo = await getApi().register({ email: email.trim(), name: name.trim(), password });
        set({ user: userInfo, isGuest: false, authView: null });
        (get() as AuthStore).resetAuthForm();
      } catch (err: any) {
        set({ authError: err.message || 'Registration failed' });
      } finally {
        set({ authLoading: false });
      }
    },

    async handleForgotPassword() {
      const { email } = get() as AuthStore;
      if (!email.trim()) return;
      set({ authLoading: true, authError: null });
      try {
        await getApi().forgotPassword({ email: email.trim() });
        set({ forgotSent: true });
      } catch (err: any) {
        set({ authError: err.message || 'Request failed' });
      } finally {
        set({ authLoading: false });
      }
    },

    async handleResetPassword() {
      const { resetToken, newPassword } = get() as AuthStore;
      if (!resetToken.trim() || !newPassword) return;
      set({ authLoading: true, authError: null });
      try {
        const userInfo = await getApi().resetPassword({ token: resetToken.trim(), password: newPassword });
        set({ user: userInfo, isGuest: false, authView: null });
        (get() as AuthStore).resetAuthForm();
      } catch (err: any) {
        set({ authError: err.message || 'Reset failed' });
      } finally {
        set({ authLoading: false });
      }
    },

    async handleLogout() {
      await getApi().logout();
      const [u, guest] = await Promise.all([getApi().getUser(), getApi().isGuest()]);
      set({ user: u, isGuest: guest, authView: null });
    },
  }),
});

export const useAuth = authModule.hooks.useAuth;
